import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Home,
  Users,
  CreditCard,
  Receipt,
  FileText,
  Clock,
  BarChart,
  ChevronDown,
  ChevronRight,
  Settings,
  LogOut,
  UserCog,
  Code,
  LayoutDashboard
} from 'lucide-react';

const Sidebar = () => {
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [timeTrackingOpen, setTimeTrackingOpen] = useState(false);
  const { user, logout } = useAuth();
  const isImpersonating = !!localStorage.getItem('impersonatorToken');

  return (
    <div className={`w-64 bg-slate-900 text-white flex flex-col fixed left-0 ${isImpersonating ? 'top-10 h-[calc(100vh-40px)]' : 'top-0 h-screen'} overflow-y-auto z-20 transition-all`}>
      <div className="p-4 flex items-center gap-2 border-b border-slate-700">
        <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center font-bold">B</div>
        <span className="text-xl font-semibold">FlowBill</span>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        <NavLink
          to="/"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-800'}`
          }
        >
          <Home size={20} />
          <span>Home</span>
        </NavLink>

        <NavLink
          to="/customers"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-800'}`
          }
        >
          <Users size={20} />
          <span>Customers</span>
        </NavLink>

        <NavLink
          to="/invoices"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-800'}`
          }
        >
          <FileText size={20} />
          <span>Invoices</span>
        </NavLink>

        <NavLink
          to="/developers"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-800'}`
          }
        >
          <Code size={20} />
          <span>Developers</span>
        </NavLink>

        <NavLink
          to="/board"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-800'}`
          }
        >
          <LayoutDashboard size={20} />
          <span>Board</span>
        </NavLink>

        {/* Payments Section */}
        <div>
          <button
            onClick={() => setPaymentsOpen(!paymentsOpen)}
            className="w-full flex items-center justify-between px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md transition-colors"
          >
            <div className="flex items-center gap-3">
              <CreditCard size={20} />
              <span>Payments</span>
            </div>
            {paymentsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {paymentsOpen && (
            <div className="ml-9 mt-1 space-y-1">
              <NavLink
                to="/payments"
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm rounded-md transition-colors ${isActive ? 'text-white' : 'text-slate-400 hover:text-white'}`
                }
              >
                Payments Received
              </NavLink>
            </div>
          )}
        </div>

        {/* Expenses Section */}
        <div>
          <button
            onClick={() => setExpensesOpen(!expensesOpen)}
            className="w-full flex items-center justify-between px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md transition-colors"
          >
            <div className="flex items-center gap-3">
              <Receipt size={20} />
              <span>Expenses</span>
            </div>
            {expensesOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {expensesOpen && (
            <div className="ml-9 mt-1 space-y-1">
              <NavLink
                to="/expenses"
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm rounded-md transition-colors ${isActive ? 'text-white' : 'text-slate-400 hover:text-white'}`
                }
              >
                Other Expenses
              </NavLink>
              <NavLink
                to="/developer-payments"
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm rounded-md transition-colors ${isActive ? 'text-white' : 'text-slate-400 hover:text-white'}`
                }
              >
                Developers Receivables
              </NavLink>
            </div>
          )}
        </div>

        {/* Time Tracking Section */}
        <div>
          <button
            onClick={() => setTimeTrackingOpen(!timeTrackingOpen)}
            className="w-full flex items-center justify-between px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md transition-colors"
          >
            <div className="flex items-center gap-3">
              <Clock size={20} />
              <span>Time Tracking</span>
            </div>
            {timeTrackingOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {timeTrackingOpen && (
            <div className="ml-9 mt-1 space-y-1">
              <NavLink
                to="/projects"
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm rounded-md transition-colors ${isActive ? 'text-white' : 'text-slate-400 hover:text-white'}`
                }
              >
                Projects
              </NavLink>
              <NavLink
                to="/timesheets"
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm rounded-md transition-colors ${isActive ? 'text-white' : 'text-slate-400 hover:text-white'}`
                }
              >
                Timesheet
              </NavLink>
            </div>
          )}
        </div>


      </nav>

      <div className="p-4 border-t border-slate-700 space-y-1">
        {user?.role?.toLowerCase() === 'admin' && (
          <NavLink
            to="/users"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-800'}`
            }
          >
            <UserCog size={20} />
            <span>Manage Users</span>
          </NavLink>
        )}

        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-800'}`
          }
        >
          <Settings size={20} />
          <span>Settings</span>
        </NavLink>

        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-slate-300 hover:text-white hover:bg-slate-800 text-left"
        >
          <LogOut size={20} />
          <span>Log Out</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
